import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Card, MiniBoard, SplitButton, Button, IconButton, TextInput, StatusPill, Text } from "components";
import {
  selectGameId,
  selectGameObject,
} from "features/gamestate/gameStateSlice";
import { selectLeaderboardPlayers } from "features/leaderboard/leaderboardSlice";
import { updateGameDetailsAction, selectSavingDetails } from "features/gameEditor/editorSlice";
import {
  getDisplayStatus,
  getPublishStatus,
  getRoundKeys,
  getRoundCount,
  computeCompletion,
  computePlayProgress,
  touchedAtMs,
} from "../lifecycle";
import Provider from "provider";
import { selectGameHomeHeroState, selectFirstIncompleteRound } from "./selectors";
import { launchGame, openBoardInNewTab } from "./launchFlow";
import { liveStateLabel, controllingPlayerName } from "./liveState";
import { statusOverrideItems } from "./statusOverride";
import logging from "utils/logging";
import "./Hero.css";

const log = logging.getLogger("Hero");

const PlayGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);
const CheckGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const PencilGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const agoLabel = (game) => {
  const ms = touchedAtMs(game);
  return ms ? moment(ms).fromNow() : "";
};

// Category labels for the hero's board thumbnail — first configured round, 4 max.
const thumbCategories = (game) => {
  const key = getRoundKeys(game)[0];
  const cats = key ? game?.categories?.[key] : [];
  return (Array.isArray(cats) ? cats : []).map((c) => c?.category || "").slice(0, 4);
};

const Hero = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const game = useSelector(selectGameObject);
  const gameId = useSelector(selectGameId);
  const heroConfig = useSelector(selectGameHomeHeroState);
  const firstIncomplete = useSelector(selectFirstIncompleteRound);
  const players = useSelector(selectLeaderboardPlayers);
  const saving = useSelector(selectSavingDetails);
  const [busy, setBusy] = useState(false);

  // Inline name/description editing (the game's identity lives on the hub now
  // that the Game Details page is retired).
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState(game?.name || "");
  const [descDraft, setDescDraft] = useState(game?.description || "");
  useEffect(() => {
    if (!editing) {
      setNameDraft(game?.name || "");
      setDescDraft(game?.description || "");
    }
  }, [game?.name, game?.description, editing]);

  if (!heroConfig || !game) return null;

  const { state, primary, secondary } = heroConfig;
  const displayStatus = getDisplayStatus(game);
  const when = agoLabel(game);

  // Eyebrow + meta line + primary-button icon + "opens board" hint, per state.
  const { written, total, percentage } = computeCompletion(game);
  const roundCount = getRoundCount(game);
  const roundsLabel = `${roundCount} ${roundCount === 1 ? "round" : "rounds"}`;

  let eyebrow = "";
  let meta = "";
  let icon = null;
  let hint = false;

  if (state === "draft") {
    eyebrow = "Keep building";
    meta = `${written}/${total} clues written · ${percentage}% complete${when ? ` · last edit ${when}` : ""}`;
    icon = <PencilGlyph />;
  } else if (state === "awaiting") {
    eyebrow = "All clues written — ready to publish?";
    meta = `${written} clues written${when ? ` · last edit ${when}` : ""}`;
    icon = <CheckGlyph />;
  } else if (state === "ready") {
    eyebrow = "Ready to launch";
    meta = `${roundsLabel} · ${written} clues${when ? ` · marked ready ${when}` : ""}`;
    icon = <PlayGlyph />;
    hint = true;
  } else if (state === "in-progress") {
    const { played, total: playTotal } = computePlayProgress(game);
    const live = liveStateLabel(game, {
      selectingPlayer: controllingPlayerName(game, players),
    });
    eyebrow =
      live && live !== "In progress"
        ? `Game in progress · ${live}`
        : "Game in progress";
    meta = `${played}/${playTotal} clues played${when ? ` · started ${when}` : ""}`;
    icon = <PlayGlyph />;
  } else if (state === "played") {
    const winner = players?.[0];
    const winnerName = winner?.displayName || winner?.name;
    const count = players?.length || 0;
    eyebrow = "Played";
    meta = winnerName
      ? `${winnerName} won $${(winner.score || 0).toLocaleString()}${count ? ` · ${count} ${count === 1 ? "player" : "players"}` : ""}`
      : (when ? `Played ${when}` : "Played");
    icon = <PlayGlyph />;
    hint = true;
  } else if (state === "archived") {
    eyebrow = "Archived";
    meta = when ? `Archived ${when}` : "This game is archived.";
  }

  const wrap = async (fn) => {
    setBusy(true);
    try { await fn(); }
    catch (err) { log.error("Hero action failed", err); }
    finally { setBusy(false); }
  };

  const handlePrimary = () => {
    if (state === "draft") {
      navigate(`/manage/game/${gameId}/editor/${firstIncomplete || "round1"}`);
    } else if (state === "awaiting") {
      wrap(() => Provider.markGameReady(gameId));
    } else if (state === "ready") {
      launchGame(gameId, navigate);
    } else if (state === "in-progress") {
      navigate(`/manage/game/${gameId}/control-room`);
    } else if (state === "played") {
      openBoardInNewTab(gameId);
    } else if (state === "archived") {
      wrap(() => Provider.unarchiveGame(gameId));
    }
  };

  const handleSecondary = () => {
    if (state === "awaiting") {
      navigate(`/manage/game/${gameId}/editor/${firstIncomplete || "round1"}`);
    } else if (state === "ready" || state === "in-progress") {
      openBoardInNewTab(gameId);
    } else if (state === "played") {
      // Copy & Replay: copy then navigate to the new draft's editor.
      wrap(async () => {
        const result = await Provider.copyGame(gameId, `Copy of ${game.name || "Untitled Game"}`);
        const newGameId = result?.data?.gameId;
        if (newGameId) navigate(`/manage/game/${newGameId}/details`);
      });
    }
  };

  const saveIdentity = async () => {
    const name = nameDraft.trim() || game.name || "Untitled game";
    await dispatch(updateGameDetailsAction(gameId, { name, description: descDraft }));
    setEditing(false);
  };
  const cancelEdit = () => {
    setNameDraft(game.name || "");
    setDescDraft(game.description || "");
    setEditing(false);
  };

  // Caret menu = this state's secondary action (if any) + manual status
  // overrides (archive/restore stay in Settings, so skip overrides there).
  const overrideItems =
    state === "archived"
      ? []
      : statusOverrideItems(getPublishStatus(game), gameId, (err) =>
          log.error("Status override failed", err)
        );
  const caretItems = [
    ...(secondary ? [{ label: secondary.label, onClick: handleSecondary }] : []),
    ...overrideItems,
  ];

  return (
    <Card spotlight padding="lg" className="gx-ghero">
      <div className="gx-ghero-thumb">
        <MiniBoard categories={thumbCategories(game)} values={[200, 400, 600]} />
      </div>

      {editing ? (
        <div className="gx-ghero-body gx-ghero-edit">
          <TextInput
            label="Game name"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            disabled={saving}
            autoFocus
          />
          <TextInput
            label="Description"
            multiline
            rows={3}
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            placeholder="What's this game about?"
            disabled={saving}
          />
          <div className="gx-ghero-edit-actions">
            <Button variant="primary" size="md" loading={saving} onClick={saveIdentity}>
              Save
            </Button>
            <Button variant="secondary" size="md" disabled={saving} onClick={cancelEdit}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="gx-ghero-body">
            <Text variant="eyebrow" tone="accent" className="gx-ghero-eyebrow">{eyebrow}</Text>
            <div className="gx-ghero-titlerow">
              <Text variant="h1" className="gx-ghero-title">{game.name || "Untitled game"}</Text>
              <StatusPill status={displayStatus === "awaiting" ? "awaiting-review" : displayStatus} />
              <IconButton variant="ghost" label="Edit name & description" onClick={() => setEditing(true)}>
                <PencilGlyph />
              </IconButton>
            </div>
            {meta && <Text variant="body" tone="muted" className="gx-ghero-meta">{meta}</Text>}
            {game.description && (
              <Text variant="caption" tone="muted" className="gx-ghero-desc">{game.description}</Text>
            )}
          </div>

          <div className="gx-ghero-cta">
            {caretItems.length > 0 ? (
              <SplitButton
                variant="primary"
                size="lg"
                startIcon={icon}
                onClick={handlePrimary}
                disabled={busy}
                items={caretItems}
              >
                {primary.label}
              </SplitButton>
            ) : (
              <Button
                variant="primary"
                size="lg"
                startIcon={icon}
                onClick={handlePrimary}
                loading={busy}
              >
                {primary.label}
              </Button>
            )}
            {hint && (
              <Text variant="caption" tone="muted" className="gx-ghero-hint">
                Opens the game board in a new tab.
              </Text>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default Hero;
